import flatten from 'lodash/flatten';
import get from 'lodash/get';
import { MutationType, STPageType } from '@tencent/slide-sdk-pb-model';
import { ShapeDataModel } from '../model/shape-data-model';
import { TableDataModel } from '../model/table-data-model';
import { GroupShapeDataModel } from '../model/group-shape-data-model';
import { PageDataModel } from '../model/page-data-model';
import { PresentationDataModel } from '../model/presentation-data-model';
import { TextDataUtils } from '../mutation/text/set-text-data-mutation';
import { TextPlaceholderType } from '../constants/text-constants';
import { PresentationEventTypes } from './event-manager/event-types';
import type { IEventManager } from './event-manager';
import type { IRemovePageMutationData } from '@tencent/slide-sdk-pb-model';
import type {
    IValidMutationData,
    IValidAddPageMutationData,
    IValidMovePageMutationData,
    IValidRemovePageMutationData,
} from '../mutation/base/valid-mutation-data';
import type { INoteSlideDataModel } from '../model/note-slide-data-model';
import type { ILayoutDataModel } from '../model/layout-data-model';
import type { ISlideDataModel } from '../model/slide-data-model';
import type { IDrawingShapeDataModel } from '../model/group-shape-data-model';
import type { IMasterDataModel } from '../model/master-data-model';
import type { IPresentationDataModel } from '../model/presentation-data-model';
import type { Mutation } from '../mutation/base/base-mutation';

interface SearchResultItem {
    offset: number;
    paragraph: number;
    cell?: { row: number; col: number };
}

export type SearchResult = Array<{
    component: IDrawingShapeDataModel;
    uniqueID: string;
    pageId: string;
    result: Array<SearchResultItem>;
}>;

export interface ISearchManager {
    searchText(
        text: string,
        dataModels?: Array<IPresentationDataModel | ISlideDataModel | ILayoutDataModel | IMasterDataModel>,
    ): SearchResult;
    replaceSearchResultFromMutation(keyword: string, preSearchResult: SearchResult): boolean;
}

export class SearchManager implements ISearchManager {
    private curUserChange: IValidMutationData[] = [];

    constructor(private presentation: IPresentationDataModel, private events: IEventManager) {
        this.init();
    }

    /**
     * 全文搜索字符串，如果找到，返回结果格式为SearchResult
     * @param text 要搜索的内容
     * @param dataModels 可选，要搜索的范围，递归所有子元素
     */
    public searchText(
        text: string,
        dataModels?: Array<IPresentationDataModel | ISlideDataModel | ILayoutDataModel | IMasterDataModel>,
    ) {
        if (!text.length) {
            return [];
        }
        const lowercaseText = text.toLocaleLowerCase();
        const allDataModels = dataModels?.length
            ? flatten([...new Set(dataModels)].map((dataModel) => this.recurseAllComponents(dataModel)))
            : [...this.recurseAllComponents(this.presentation)];
        const searchResult: SearchResult = [];
        allDataModels.forEach((dataModel) => {
            if (dataModel instanceof ShapeDataModel) {
                const shapeDataModel = dataModel;
                // 文本框
                const setTextData = TextDataUtils.fromParagraphListData(
                    shapeDataModel.store.data.txBody?.p ?? [],
                    '',
                    '',
                );
                const textResult = this.findTextInTextArray(setTextData.text, lowercaseText);
                textResult.length &&
                    searchResult.push({
                        component: dataModel,
                        pageId: dataModel.getPage().id,
                        uniqueID: dataModel.id,
                        result: textResult,
                    });
                return;
            }
            if (dataModel instanceof TableDataModel) {
                const tableDataModel = dataModel;
                const tableResult: Array<{
                    offset: number;
                    paragraph: number;
                    cell?: { row: number; col: number };
                }> = [];
                tableDataModel.store.data.graphic?.graphicData?.tbl?.tr?.forEach((row, rowIndex) =>
                    row.tc?.forEach((cell, cellIndex) => {
                        const setTextData = TextDataUtils.fromParagraphListData(
                            cell.txBody?.p ?? [],
                            '',
                            '',
                            cellIndex,
                            rowIndex,
                        );
                        const searchResultItem = this.findTextInTextArray(
                            setTextData.text,
                            lowercaseText,
                            rowIndex,
                            cellIndex,
                        );
                        tableResult.push(...searchResultItem);
                    }),
                );
                searchResult.push({
                    component: dataModel,
                    uniqueID: dataModel.id,
                    result: tableResult,
                    pageId: dataModel.getPage().id,
                });
            }
        });

        return searchResult;
    }

    public replaceSearchResultFromMutation(keyword: string, preSearchResult: SearchResult) {
        let preSearchResultChanged = false;
        const { addSlides, removeSlides, moveSlides, shapesChange } = this.getSlideChangeMutation();
        if (removeSlides.length) {
            preSearchResultChanged ||= this.removeSearchResultWithSlide(preSearchResult, removeSlides);
        }
        if (shapesChange.length) {
            shapesChange.forEach((shapeChange) => {
                preSearchResultChanged ||= this.replaceSearchResultWithShape(preSearchResult, keyword, shapeChange);
            });
        }
        if (addSlides) {
            addSlides.forEach((addSlide) => {
                preSearchResultChanged ||= this.replaceSearchResultWithAddSlide(preSearchResult, keyword, addSlide);
            });
        }
        if (moveSlides) {
            moveSlides.forEach((moveSlide) => {
                preSearchResultChanged ||= this.replaceSearchResultWithMoveSlide(preSearchResult, keyword, moveSlide);
            });
        }
        return preSearchResultChanged;
    }

    private init() {
        this.events.on(PresentationEventTypes.CommitCommand, this.handleCommitTransaction);
        return this;
    }

    private replaceSearchResultWithMoveSlide(
        preSearchResult: SearchResult,
        keyword: string,
        moveSlide: IValidMovePageMutationData,
    ) {
        const slideId = get(moveSlide, 'pageId');
        const targetSlide = slideId && this.presentation.getSlideById(slideId);
        if (!targetSlide) return false;
        const reSearchTextResult = this.searchText(keyword, [targetSlide]);
        let hasChanged = false;
        reSearchTextResult.forEach((searchItem) => {
            while (preSearchResult.findIndex(({ pageId }) => pageId === searchItem.pageId) > -1) {
                preSearchResult.splice(
                    preSearchResult.findIndex(({ pageId }) => pageId === searchItem.pageId),
                    1,
                );
                hasChanged = true;
            }
        });
        preSearchResult.splice(0, 0, ...reSearchTextResult);
        return hasChanged;
    }

    private replaceSearchResultWithAddSlide(
        preSearchResult: SearchResult,
        keyword: string,
        addSlide: IValidAddPageMutationData,
    ) {
        const slideId = get(addSlide, 'pageId');
        const targetSlide = slideId && this.presentation.getSlideById(slideId);
        if (!targetSlide) return false;
        const reSearchTextResult = this.searchText(keyword, [targetSlide]);
        preSearchResult.splice(0, 0, ...reSearchTextResult);
        return true;
    }

    private replaceSearchResultWithShape(
        preSearchResult: SearchResult,
        keyword: string,
        shapeChange: IValidMutationData,
    ) {
        const slideId = get(shapeChange, 'pageId');
        const targetSlide = slideId && this.presentation.getSlideById(slideId);
        if (!targetSlide) return false;
        const reSearchTextResult = this.searchText(keyword, [targetSlide]);
        let hasChanged = false;
        reSearchTextResult.forEach((searchItem) => {
            while (preSearchResult.findIndex(({ pageId }) => pageId === searchItem.pageId) > -1) {
                preSearchResult.splice(
                    preSearchResult.findIndex(({ pageId }) => pageId === searchItem.pageId),
                    1,
                );
                hasChanged = true;
            }
        });
        preSearchResult.splice(0, 0, ...reSearchTextResult);
        return hasChanged;
    }

    private getSlideChangeMutation() {
        const slideChangeMutData = this.curUserChange.filter((mutData) => {
            const hasPageType = get(mutData, 'pageType');
            if (hasPageType && hasPageType !== STPageType.slide) return false;
            return !!get(mutData, 'pageId', '');
        });
        const addSlides: IValidAddPageMutationData[] = [];
        const removeSlides: IValidRemovePageMutationData[] = [];
        const moveSlides: IValidMovePageMutationData[] = [];
        const shapesChange: IValidMutationData[] = [];
        slideChangeMutData.forEach((mutData) => {
            if (mutData.type === MutationType.AddPage) {
                addSlides.push(mutData as IValidAddPageMutationData);
                return;
            }
            if (mutData.type === MutationType.RemovePage) {
                removeSlides.push(mutData as IValidRemovePageMutationData);
                return;
            }
            if (mutData.type === MutationType.MovePage) {
                moveSlides.push(mutData as IValidMovePageMutationData);
                return;
            }
            shapesChange.push(mutData);
        });
        return {
            addSlides,
            removeSlides,
            moveSlides,
            shapesChange,
        };
    }

    private findTextInTextArray(paragraphText: string, text: string, rowIndex?: number, cellIndex?: number) {
        // 获取每个单元格的每段文本平铺后的数组
        const paragraphTextArray = paragraphText.split(TextPlaceholderType.kParagraphReturn);
        const result: SearchResultItem[] = [];
        paragraphTextArray.forEach((paragraphText, index) => {
            let offset = -1;
            const lowercaseParagraphText = paragraphText.toLocaleLowerCase();
            while ((offset = lowercaseParagraphText.indexOf(text, offset)) !== -1) {
                const searchResultItem: SearchResultItem = {
                    offset,
                    paragraph: index,
                };
                if (rowIndex !== undefined && cellIndex !== undefined) {
                    searchResultItem.cell = { row: rowIndex, col: cellIndex };
                }
                result.push(searchResultItem);
                offset += text.length;
            }
        });
        return result;
    }

    /**
     * 递归可被查找的元素，包括文本框、形状、表格、备注
     * @param acc
     * @param component
     */
    private recurseAllComponents = (
        dataModel:
            | IPresentationDataModel
            | IDrawingShapeDataModel
            | ISlideDataModel
            | ILayoutDataModel
            | IMasterDataModel
            | INoteSlideDataModel,
    ) => {
        const dataModels: Array<ShapeDataModel | TableDataModel> = [];
        if (dataModel instanceof PresentationDataModel) {
            dataModels.push(
                ...flatten(dataModel.slideList.map(this.recurseAllComponents)),
                // ...flatten(dataModel.masterList.map(this.recurseAllComponents)),
                // ...flatten(dataModel.layoutList.map(this.recurseAllComponents)),
                ...flatten([...dataModel.noteSlideMap.values()].map(this.recurseAllComponents)),
            );
        } else if (dataModel instanceof ShapeDataModel || dataModel instanceof TableDataModel) {
            // 加入文本框
            dataModels.push(dataModel);
        } else if (dataModel instanceof GroupShapeDataModel || dataModel instanceof PageDataModel) {
            dataModels.push(...flatten(dataModel.shapeList.map(this.recurseAllComponents)));
        }
        return dataModels;
    };

    private handleCommitTransaction = (mutations: Mutation[]) => {
        this.curUserChange = mutations.map((mut) => mut.toData());
    };

    private removeSearchResultWithSlide(preSearchResult: SearchResult, removedSlides: IRemovePageMutationData[]) {
        const removedSlideIds = removedSlides.map((slide) => slide.pageId || '');
        let hasChanged = false;
        removedSlideIds.forEach((removedSlideId) => {
            while (preSearchResult.findIndex(({ pageId }) => pageId === removedSlideId) > -1) {
                preSearchResult.splice(
                    preSearchResult.findIndex(({ pageId }) => pageId === removedSlideId),
                    1,
                );
                hasChanged = true;
            }
        });
        return hasChanged;
    }
}
